// Helvetica as a PDF reader sets it, for laying out type without a browser.
//
// A PDF's built-in Helvetica isn't stored in the file: the reader draws it from
// its own copy, at the widths Adobe published for it. Lines are measured here
// with those widths, so text cut or wrapped to fit a box prints exactly as wide
// as it was measured. jsPDF's own tables round each width to a hundredth of an
// em, which over a line of small type comes to a couple of points — enough to
// clip the last letter at the edge of a box.
//
// The built-in fonts reach only the WinAnsi character set: Latin-1 and a few
// more. Anything outside it prints as a question mark rather than as garbage.

// Advance widths in thousandths of an em for WinAnsi 0x20 to 0xFF, from
// Adobe's Helvetica and Helvetica-Bold metrics; 0 marks a code WinAnsi leaves
// empty. The oblique faces have the same widths as the upright ones.
const REGULAR = [
  /* 0x20 */ 278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278,
  /* 0x30 */ 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584, 584, 556,
  /* 0x40 */ 1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778,
  /* 0x50 */ 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556,
  /* 0x60 */ 333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556,
  /* 0x70 */ 556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584, 0,
  /* 0x80 */ 556, 0, 222, 556, 333, 1000, 556, 556, 333, 1000, 667, 333, 1000, 0, 611, 0,
  /* 0x90 */ 0, 222, 222, 333, 333, 350, 556, 1000, 333, 1000, 500, 333, 944, 0, 500, 500,
  /* 0xA0 */ 278, 333, 556, 556, 556, 556, 260, 556, 333, 737, 370, 556, 584, 333, 737, 333,
  /* 0xB0 */ 400, 584, 333, 333, 333, 556, 537, 278, 333, 333, 365, 556, 834, 834, 834, 611,
  /* 0xC0 */ 667, 667, 667, 667, 667, 667, 1000, 722, 667, 667, 667, 667, 278, 278, 278, 278,
  /* 0xD0 */ 722, 722, 778, 778, 778, 778, 778, 584, 778, 722, 722, 722, 722, 667, 667, 611,
  /* 0xE0 */ 556, 556, 556, 556, 556, 556, 889, 500, 556, 556, 556, 556, 278, 278, 278, 278,
  /* 0xF0 */ 556, 556, 556, 556, 556, 556, 556, 584, 611, 556, 556, 556, 556, 500, 556, 500,
];

const BOLD = [
  /* 0x20 */ 278, 333, 474, 556, 556, 889, 722, 238, 333, 333, 389, 584, 278, 333, 278, 278,
  /* 0x30 */ 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 333, 333, 584, 584, 584, 611,
  /* 0x40 */ 975, 722, 722, 722, 722, 667, 611, 778, 722, 278, 556, 722, 611, 833, 722, 778,
  /* 0x50 */ 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 333, 278, 333, 584, 556,
  /* 0x60 */ 333, 556, 611, 556, 611, 556, 333, 611, 611, 278, 278, 556, 278, 889, 611, 611,
  /* 0x70 */ 611, 611, 389, 556, 333, 611, 556, 778, 556, 556, 500, 389, 280, 389, 584, 0,
  /* 0x80 */ 556, 0, 278, 556, 500, 1000, 556, 556, 333, 1000, 667, 333, 1000, 0, 611, 0,
  /* 0x90 */ 0, 278, 278, 500, 500, 350, 556, 1000, 333, 1000, 556, 333, 944, 0, 500, 556,
  /* 0xA0 */ 278, 333, 556, 556, 556, 556, 280, 556, 333, 737, 370, 556, 584, 333, 737, 333,
  /* 0xB0 */ 400, 584, 333, 333, 333, 611, 556, 278, 333, 333, 365, 556, 834, 834, 834, 611,
  /* 0xC0 */ 722, 722, 722, 722, 722, 722, 1000, 722, 667, 667, 667, 667, 278, 278, 278, 278,
  /* 0xD0 */ 722, 722, 778, 778, 778, 778, 778, 584, 778, 722, 722, 722, 722, 667, 667, 611,
  /* 0xE0 */ 556, 556, 556, 556, 556, 556, 889, 556, 556, 556, 556, 556, 278, 278, 278, 278,
  /* 0xF0 */ 611, 611, 611, 611, 611, 611, 611, 584, 611, 611, 611, 611, 611, 556, 611, 556,
];

// The code points WinAnsi puts at 0x80 to 0x9F, where Latin-1 has control codes.
const WIN_ANSI_EXTRAS = new Map([
  [0x20ac, 0x80], [0x201a, 0x82], [0x0192, 0x83], [0x201e, 0x84],
  [0x2026, 0x85], [0x2020, 0x86], [0x2021, 0x87], [0x02c6, 0x88],
  [0x2030, 0x89], [0x0160, 0x8a], [0x2039, 0x8b], [0x0152, 0x8c],
  [0x017d, 0x8e], [0x2018, 0x91], [0x2019, 0x92], [0x201c, 0x93],
  [0x201d, 0x94], [0x2022, 0x95], [0x2013, 0x96], [0x2014, 0x97],
  [0x02dc, 0x98], [0x2122, 0x99], [0x0161, 0x9a], [0x203a, 0x9b],
  [0x0153, 0x9c], [0x017e, 0x9e], [0x0178, 0x9f],
]);

// The WinAnsi code for a printable character, or null when it has none.
const winAnsiCode = (codePoint) => {
  if ((codePoint >= 0x20 && codePoint <= 0x7e) || (codePoint >= 0xa0 && codePoint <= 0xff)) return codePoint;
  return WIN_ANSI_EXTRAS.get(codePoint) ?? null;
};

// Characters WinAnsi lacks that print as one it has: the spaces and hyphens
// that keyboards and phones slip in.
const STAND_INS = new Map([
  [' ', ' '], [' ', ' '], [' ', ' '], [' ', ' '], [' ', ' '], [' ', ' '],
  ['‐', '-'], ['‑', '-'], ['−', '-'],
]);

// Characters that take no room on screen: zero-width spaces and joiners, and
// the soft hyphen, which only shows where a browser breaks a word at it.
const INVISIBLE = /[­​-‍⁠﻿]/g;

// `text` as the built-in fonts can print it: accents composed onto their
// letters, and anything WinAnsi has no glyph for replaced. The control codes
// CSS counts as white space (tab, line feed, carriage return and form feed)
// are left for whatever sets the text out; the others are dropped.
export const toWinAnsi = (text) =>
  Array.from(String(text ?? '').normalize('NFC').replace(INVISIBLE, ''), (char) => {
    if (STAND_INS.has(char)) return STAND_INS.get(char);
    const codePoint = char.codePointAt(0);
    if (codePoint < 0x20) return '\t\n\r\f'.includes(char) ? char : '';
    return winAnsiCode(codePoint) === null ? '?' : char;
  }).join('');

// How wide `text` (already passed through toWinAnsi) sets, in points.
export const textWidth = (text, fontSize, bold = false) => {
  const widths = bold ? BOLD : REGULAR;
  let total = 0;
  for (const char of text) {
    const code = winAnsiCode(char.codePointAt(0));
    if (code !== null) total += widths[code - 0x20];
  }
  return (total * fontSize) / 1000;
};
